import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import type {
  MessageThreadListResponse,
  MessageThreadResponse,
  MessageThreadView,
} from "../../../shared/api-types";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import {
  messagesRefetchInterval,
  type PollReadState,
  messageThreadQueryKey,
  messagesQueryKey,
  withOlderPage,
  type OlderMessages,
} from "@/lib/messages-client";

// The Messages list (GET /api/messages/threads) and one Client's threads
// (GET /api/messages/threads/:patientId), NO-925. `rdId` is an admin's picked
// RD and rides as `?rdId=`; nobody else sends it. Only the pages' gate mounts
// a caller, and only once the page may show, so a viewer the flag hides never
// asks. Message text is PHI: `gcTime: 0` drops it from the cache when the page
// unmounts, and no key holds a Client name or message text.

/** A query's read state for the poll rules (`messages-client.ts`). */
export function pollStateOf(query: {
  data: unknown;
  error: unknown;
  isError?: boolean;
  status?: string;
}): PollReadState {
  const isError = query.isError ?? query.status === "error";

  return {
    isError,
    hasData: query.data !== undefined,
    errorStatus: query.error instanceof ApiRequestError ? query.error.status : null,
  };
}

function rdQuery(rdId: string | undefined): string {
  return rdId ? `?rdId=${encodeURIComponent(rdId)}` : "";
}

export function useMessageList(rdId: string | undefined) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: messagesQueryKey(rdId),
    queryFn: async () =>
      (
        await authenticatedApi<{ data: MessageThreadListResponse }>(
          getToken,
          `/api/messages/threads${rdQuery(rdId)}`
        )
      ).data,
    gcTime: 0,
    refetchInterval: (query) => messagesRefetchInterval(pollStateOf(query.state)),
  });
}

export function useClientThreads(patientId: string, rdId: string | undefined) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: messageThreadQueryKey(patientId, rdId),
    queryFn: async () =>
      (
        await authenticatedApi<{ data: MessageThreadResponse }>(
          getToken,
          `/api/messages/threads/${encodeURIComponent(patientId)}${rdQuery(rdId)}`
        )
      ).data,
    gcTime: 0,
    refetchInterval: (query) => messagesRefetchInterval(pollStateOf(query.state)),
  });
}

type GetToken = ReturnType<typeof useAuth>["getToken"];

/** One older page of one thread; throws when the server returns anything else. */
async function fetchOlderPage(
  getToken: GetToken,
  query: {
    patientId: string;
    rdId: string | undefined;
    threadId: string;
    cursor: string;
  }
): Promise<MessageThreadView> {
  const params = new URLSearchParams({
    threadId: query.threadId,
    before: query.cursor,
  });
  if (query.rdId) params.set("rdId", query.rdId);

  const { data } = await authenticatedApi<{ data: MessageThreadResponse }>(
    getToken,
    `/api/messages/threads/${encodeURIComponent(query.patientId)}?${params.toString()}`
  );
  const page =
    data.status === "ok"
      ? data.threads.find((thread) => thread.threadId === query.threadId)
      : undefined;
  if (!page) throw new Error("older page missing");

  return page;
}

const NO_OLDER: Record<string, OlderMessages> = {};

/**
 * "Load older messages" (NO-925): one thread's page of messages older than the
 * cursor, from the same route. Each page is its own read on the server (scope
 * check and Clinical Read Event). Held in component state, so nothing outlives
 * the page.
 */
export function useOlderMessages(
  patientId: string,
  rdId: string | undefined,
  firstPageKey: string
) {
  const { getToken } = useAuth();
  const [held, setHeld] = useState<{
    key: string;
    byThread: Record<string, OlderMessages>;
  }>({
    key: firstPageKey,
    byThread: {},
  });
  // Older pages sit above one first page: a different first page drops them.
  const older = held.key === firstPageKey ? held.byThread : NO_OLDER;
  const setOlder = useCallback(
    (update: (prev: Record<string, OlderMessages>) => Record<string, OlderMessages>) =>
      setHeld((current) => ({
        key: firstPageKey,
        byThread: update(current.key === firstPageKey ? current.byThread : NO_OLDER),
      })),
    [firstPageKey]
  );
  const load = useCallback(
    async (threadId: string, cursor: string) => {
      setOlder((prev) => ({
        ...prev,
        [threadId]: {
          messages: prev[threadId]?.messages ?? [],
          cursor,
          status: "loading",
        },
      }));

      try {
        const page = await fetchOlderPage(getToken, {
          patientId,
          rdId,
          threadId,
          cursor,
        });

        setOlder((prev) => ({
          ...prev,
          [threadId]: withOlderPage(prev[threadId], page),
        }));
      } catch {
        setOlder((prev) => ({
          ...prev,
          [threadId]: {
            messages: prev[threadId]?.messages ?? [],
            cursor,
            status: "failed",
          },
        }));
      }
    },
    [getToken, patientId, rdId, setOlder]
  );

  return { older, load };
}
